import { Injectable } from '@angular/core';
import { BaseApiService } from './base-api-service';
import { Observable, tap } from 'rxjs';
import { environment } from '../../environments/environment';
import { StorageMap } from '@ngx-pwa/local-storage';
import { HttpClient } from '@angular/common/http';
import { BusyService } from './busy-service';
import { firstValueFrom } from 'rxjs';
import { CustomerSelectionService } from './customer-selection';
import { RouteSelectionService } from './route-selection';

export interface UserInfo {
  user: string;
  routeIds: number[];
  isSalesAdmin: boolean;
  isDeliveryBoy: boolean;
  isDeliverySupervisor: boolean;
  isLoadManager: boolean;
  isPacker: boolean;
}

export interface LoginResponse {
  status_code: string;
  message: {
    auth_token: string;
    userInfo: UserInfo;
  };
}

@Injectable({
  providedIn: 'root'
})
export class AuthService extends BaseApiService {
  private readonly dbName = environment.dbName;

  constructor(
    private storage: StorageMap,
    http: HttpClient,
    busyService: BusyService,
    private customerSelectionService: CustomerSelectionService,
    private routeSelectionService: RouteSelectionService
  ) {
    super(http, busyService);
  }

  login(credentials: any): Observable<LoginResponse> {
    credentials.db = this.dbName;

    return this.post<LoginResponse>('authenticate', credentials).pipe(
      tap(async response => {
        const { auth_token: token, userInfo } = response.message || {};

        if (token) {
          try {
            await firstValueFrom(this.storage.set('userInfo', userInfo));
            await firstValueFrom(this.storage.set('auth_token', token));
          } catch (err) {
            console.error('Failed to persist login credentials:', err);
          }
        }
      })
    );
  }

  logout(): void {
    firstValueFrom(this.storage.delete('auth_token')).catch(err =>
      console.error('Logout cleanup failed:', err)
    );

    firstValueFrom(this.storage.delete('userInfo')).catch(err =>
      console.error('Logout cleanup for user failed:', err)
    );

    this.customerSelectionService.clearSelected();
    this.routeSelectionService.clearSelected();
  }

  getToken(): Observable<string | undefined> {
    return this.storage.get('auth_token') as Observable<string | undefined>;
  }

  getCurrentUser(): Observable<UserInfo | undefined> {
    return this.storage.get('userInfo') as Observable<UserInfo | undefined>;
  }

  async updateUserInfo(): Promise<void> {
    try {
      const latestUserInfo = await firstValueFrom(this.post<UserInfo>('user_info', {}));
      await firstValueFrom(this.storage.set('userInfo', latestUserInfo));
    } catch (err) {
      console.error('Failed to refresh user info:', err);
    }
  }
}
